/**
 * PdfTemplates page — list & create PDF templates per document kind.
 * Clicking a template opens the visual editor (TemplateEditor).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Plus, Check, Trash2, Copy, Sparkles, Layers, Share2, Pencil,
} from 'lucide-react';

import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  getTemplateRegistry, listTemplates, createTemplate, deleteTemplate, activateTemplate, duplicateTemplate,
  listLetterheads, createLetterhead, deleteLetterhead, shareLetterhead,
  getLetterhead, updateLetterhead, uploadLetterheadFile, deleteLetterheadFile, fetchLetterheadPreviewUrl,
} from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import TemplateEditor from './TemplateEditor';

// Adapter wired into TemplateEditor when editing a shared letterhead —
// same UI, different REST endpoints. `supportsActivate=false` hides the
// "Set as active" button (letterheads aren't kind-scoped); `fixedKind`
// is what the registry lookup needs since the API doc doesn't carry one.
const LETTERHEAD_ADAPTER = {
  getOne: getLetterhead,
  update: updateLetterhead,
  uploadFile: uploadLetterheadFile,
  deleteFile: deleteLetterheadFile,
  fetchPreviewUrl: fetchLetterheadPreviewUrl,
  activate: null, deactivate: null,
  supportsActivate: false,
  fixedKind: 'letterhead',
  letterheadLabel: 'background PDF',
  // Override for the rasterised first-page-of-letterhead image. The
  // editor uses this URL as an `<img src>` so chips and the background
  // are scaled by the same parent — no more zoom drift.
  letterheadImagePath: (id) => `/api/pdf-letterheads/${id}/letterhead-image`,
};

const PdfTemplates = () => {
  const [registry, setRegistry] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [letterheads, setLetterheads] = useState([]);
  const [creating, setCreating] = useState({ open: false, kind: '', name: '' });
  const [copying, setCopying] = useState({ open: false, source: null, kind: '', name: '' });
  const [creatingLh, setCreatingLh] = useState({ open: false, name: '' });
  const [sharing, setSharing] = useState({ open: false, lh: null, selected: new Set() });
  const [editingId, setEditingId] = useState(null);
  // When non-null, the same TemplateEditor is opened for a letterhead
  // doc using the LETTERHEAD_ADAPTER above.
  const [editingLetterheadId, setEditingLetterheadId] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const refresh = async () => {
    try {
      const [reg, tpls, lhs] = await Promise.all([
        getTemplateRegistry(),
        listTemplates(),
        listLetterheads(),
      ]);
      setRegistry(reg);
      setTemplates(tpls);
      setLetterheads(lhs);
      setLoadError(null);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || 'Failed to load templates');
    }
  };

  useEffect(() => { refresh(); }, []);

  const templatesByKind = useMemo(() => {
    const out = {};
    for (const t of templates) {
      (out[t.kind] ||= []).push(t);
    }
    return out;
  }, [templates]);

  const openCreate = (kind) => setCreating({ open: true, kind, name: '' });

  const submitCreate = async () => {
    if (!creating.name.trim()) { toast.error('Give the template a name'); return; }
    try {
      const tpl = await createTemplate({ kind: creating.kind, name: creating.name.trim() });
      setCreating({ open: false, kind: '', name: '' });
      await refresh();
      toast.success(`${creating.kind} template created`);
      setEditingId(tpl.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  const removeOne = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This can't be undone.`)) return;
    try {
      await deleteTemplate(t.id);
      await refresh();
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const openCopy = (t) => setCopying({
    open: true, source: t, kind: t.kind, name: `${t.name} (copy)`,
  });
  const submitCopy = async () => {
    const { source, kind, name } = copying;
    if (!source) return;
    if (!name.trim()) { toast.error('Give the copy a name'); return; }
    if (!kind) { toast.error('Pick a target section'); return; }
    try {
      const copy = await duplicateTemplate(source.id, { kind, name: name.trim() });
      setCopying({ open: false, source: null, kind: '', name: '' });
      await refresh();
      toast.success(
        kind === source.kind
          ? `Copied "${source.name}"`
          : `Copied "${source.name}" → ${kind}`,
      );
      setEditingId(copy.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Copy failed');
    }
  };

  const activate = async (t) => {
    try {
      await activateTemplate(t.id);
      await refresh();
      toast.success(`${t.name} is now active for ${t.kind}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Activate failed');
    }
  };

  // ---- Shared letterheads ----
  const submitCreateLh = async () => {
    if (!creatingLh.name.trim()) { toast.error('Give the letterhead a name'); return; }
    try {
      const lh = await createLetterhead({ name: creatingLh.name.trim() });
      setCreatingLh({ open: false, name: '' });
      await refresh();
      toast.success('Letterhead created — upload a PDF and position your branding');
      setEditingLetterheadId(lh.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };
  const removeLetterhead = async (lh) => {
    if (!window.confirm(`Delete letterhead "${lh.name}"?`)) return;
    try {
      await deleteLetterhead(lh.id);
      await refresh();
      toast.success('Letterhead deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };
  const openShare = (lh) => {
    // Pre-tick the templates already using this letterhead so the
    // checkbox grid reflects current state at a glance.
    const selected = new Set(templates.filter((t) => t.letterheadId === lh.id).map((t) => t.id));
    setSharing({ open: true, lh, selected });
  };
  const toggleShareSelection = (templateId) => {
    setSharing((s) => {
      const next = new Set(s.selected);
      if (next.has(templateId)) next.delete(templateId); else next.add(templateId);
      return { ...s, selected: next };
    });
  };
  const submitShare = async () => {
    const { lh, selected } = sharing;
    if (!lh) return;
    const wantOn = Array.from(selected);
    const previouslyOn = templates.filter((t) => t.letterheadId === lh.id).map((t) => t.id);
    const toAttach = wantOn.filter((id) => !previouslyOn.includes(id));
    const toDetach = previouslyOn.filter((id) => !wantOn.includes(id));
    try {
      if (toAttach.length) {
        await shareLetterhead(lh.id, { templateIds: toAttach });
      }
      if (toDetach.length) {
        await shareLetterhead(lh.id, { templateIds: toDetach, detach: true });
      }
      setSharing({ open: false, lh: null, selected: new Set() });
      await refresh();
      toast.success(
        `${lh.name} now shared with ${wantOn.length} template${wantOn.length === 1 ? '' : 's'}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Share failed');
    }
  };

  if (editingId) {
    return (
      <TemplateEditor
        templateId={editingId}
        onBack={() => { setEditingId(null); refresh(); }}
      />
    );
  }
  if (editingLetterheadId) {
    return (
      <TemplateEditor
        templateId={editingLetterheadId}
        adapter={LETTERHEAD_ADAPTER}
        onBack={() => { setEditingLetterheadId(null); refresh(); }}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-pdf-templates">
      <PageHeader
        title="PDF Templates"
        description="Upload your own letterhead and drag placeholders onto the page. Invoices, Quotes, Work Orders, RAMS, Safety Packs and Library docs all use the active template when generated."
      />

      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-900" data-testid="tpl-load-error">
          <div className="font-semibold">Couldn’t load PDF templates</div>
          <div className="text-xs mt-1">{loadError}</div>
          <button
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 underline"
            onClick={refresh}
            data-testid="tpl-load-retry"
          >Retry</button>
        </div>
      )}

      {!loadError && !registry && (
        <div className="bg-white border border-slate-200 rounded-md px-4 py-8 text-center text-slate-500 text-sm" data-testid="tpl-loading">
          Loading templates…
        </div>
      )}

      {/* Shared letterheads — branding layer reusable across multiple
          templates. Renders before the per-kind sections so admins can
          see / edit / share letterheads first, then attach them via the
          Share button or the per-template editor. */}
      {!loadError && registry && (
        <section className="bg-white border border-slate-200 rounded-md" data-testid="letterheads-section">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2 min-w-0">
              <Layers className="h-4 w-4 text-blue-700 shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-900 truncate">Shared letterheads</h3>
                <p className="text-xs text-slate-500 truncate">
                  Re-use one branded PDF + placeholders across many templates. Update once, every linked template picks up the change.
                </p>
              </div>
            </div>
            <Button
              size="sm" variant="outline"
              onClick={() => setCreatingLh({ open: true, name: '' })}
              data-testid="lh-new"
            >
              <Plus className="h-4 w-4 mr-1" /> New letterhead
            </Button>
          </header>
          <div className="divide-y divide-slate-100">
            {letterheads.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No shared letterheads yet. Create one to upload your branded background and add company-wide placeholders.
              </div>
            )}
            {letterheads.map((lh) => (
              <div key={lh.id} className="flex items-center gap-3 px-4 py-3" data-testid={`lh-row-${lh.id}`}>
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{lh.name}</div>
                  <div className="text-xs text-slate-500">
                    {lh.letterheadFile
                      ? `PDF · ${lh.letterheadPages} page${lh.letterheadPages === 1 ? '' : 's'}`
                      : 'No PDF uploaded yet'} · used by {lh.usageCount || 0} template{(lh.usageCount || 0) === 1 ? '' : 's'}
                  </div>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setEditingLetterheadId(lh.id)}
                  data-testid={`lh-edit-${lh.id}`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => openShare(lh)}
                  data-testid={`lh-share-${lh.id}`}
                >
                  <Share2 className="h-3.5 w-3.5 mr-1" /> Share
                </Button>
                <Button
                  size="sm" variant="ghost" className="text-red-600"
                  onClick={() => removeLetterhead(lh)}
                  data-testid={`lh-delete-${lh.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
      {registry?.kinds.map((k) => {
        const list = templatesByKind[k.kind] || [];
        // Backend `pdf_routes.py` wires the active template for all
        // six kinds. Anything outside this set saves the design but
        // never reaches a PDF endpoint, so we'd show an "Editor only"
        // badge there. Today every registered kind is wired — keeping
        // the gate explicit makes it obvious what to update if the
        // registry grows.
        const WIRED_KINDS = new Set(['invoice', 'quote', 'work_order', 'rams', 'safety_pack', 'library']);
        const wired = WIRED_KINDS.has(k.kind);
        return (
          <section key={k.kind} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`tpl-section-${k.kind}`}>
            <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <h3 className="font-semibold text-slate-900">{k.label}</h3>
                {wired ? (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 flex items-center gap-1"
                    data-testid={`tpl-status-wired-${k.kind}`}
                  >
                    <Check className="h-3 w-3" /> Active template applied to {k.label.toLowerCase()} PDFs
                  </span>
                ) : (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1"
                    data-testid={`tpl-status-pending-${k.kind}`}
                  >
                    <Sparkles className="h-3 w-3" /> Editor only · PDF endpoint not yet wired
                  </span>
                )}
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => openCreate(k.kind)}
                data-testid={`tpl-new-${k.kind}`}
              >
                <Plus className="h-4 w-4 mr-1" /> New template
              </Button>
            </header>
            <div className="divide-y divide-slate-100">
              {list.length === 0 && (
                <div className="px-4 py-6 text-sm text-slate-500 text-center">
                  No {k.label.toLowerCase()} templates yet. Create one to get started.
                </div>
              )}
              {list.map((t) => (
                <div key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`tpl-row-${t.id}`}>
                  <button
                    onClick={() => setEditingId(t.id)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`tpl-edit-${t.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">{t.name}</span>
                      {t.active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1">
                          <Check className="h-3 w-3" /> Active
                        </span>
                      )}
                      {t.letterheadFile && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                          {t.letterheadPages} page{t.letterheadPages !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {(t.placeholders || []).length} placeholder{(t.placeholders || []).length !== 1 ? 's' : ''} · Updated {t.updatedAt?.slice(0, 10)}
                    </div>
                  </button>
                  {!t.active && (
                    <Button size="sm" variant="outline" onClick={() => activate(t)} data-testid={`tpl-activate-${t.id}`}>
                      Set active
                    </Button>
                  )}
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => openCopy(t)}
                    data-testid={`tpl-copy-${t.id}`}
                    title="Copy to another section"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeOne(t)} data-testid={`tpl-delete-${t.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {/* Create dialog */}
      <Dialog open={creating.open} onOpenChange={(o) => { if (!o) setCreating({ open: false, kind: '', name: '' }); }}>
        <DialogContent data-testid="tpl-create-dialog">
          <DialogHeader>
            <DialogTitle>New {creating.kind} template</DialogTitle>
            <DialogDescription>
              A fresh template is seeded with every available placeholder at sensible defaults.
              You can reposition them in the editor, then upload your branded letterhead PDF.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Template name</Label>
            <Input
              autoFocus
              value={creating.name}
              onChange={(e) => setCreating({ ...creating, name: e.target.value })}
              placeholder={`e.g. Lutrom ${creating.kind} 2026`}
              data-testid="tpl-create-name"
              onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating({ open: false, kind: '', name: '' })}>Cancel</Button>
            <Button onClick={submitCreate} data-testid="tpl-create-submit">Create & open editor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy / duplicate dialog */}
      <Dialog open={copying.open} onOpenChange={(o) => { if (!o) setCopying({ open: false, source: null, kind: '', name: '' }); }}>
        <DialogContent data-testid="tpl-copy-dialog">
          <DialogHeader>
            <DialogTitle>Copy "{copying.source?.name}"</DialogTitle>
            <DialogDescription>
              Makes an editable duplicate — letterhead, placeholders, and margins are copied.
              Pasting onto a different section keeps only the placeholders that apply to the new kind and seeds any missing ones at their defaults.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Paste into section</Label>
              <Select
                value={copying.kind}
                onValueChange={(v) => setCopying((c) => ({ ...c, kind: v }))}
              >
                <SelectTrigger className="mt-1.5" data-testid="tpl-copy-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(registry?.kinds || []).map((k) => (
                    <SelectItem key={k.kind} value={k.kind}>
                      {k.label}{k.kind === copying.source?.kind ? ' (same)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">New template name</Label>
              <Input
                value={copying.name}
                onChange={(e) => setCopying({ ...copying, name: e.target.value })}
                data-testid="tpl-copy-name"
                onKeyDown={(e) => { if (e.key === 'Enter') submitCopy(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopying({ open: false, source: null, kind: '', name: '' })}>Cancel</Button>
            <Button onClick={submitCopy} data-testid="tpl-copy-submit">Copy & open editor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create letterhead dialog */}
      <Dialog open={creatingLh.open} onOpenChange={(o) => { if (!o) setCreatingLh({ open: false, name: '' }); }}>
        <DialogContent data-testid="lh-create-dialog">
          <DialogHeader>
            <DialogTitle>New shared letterhead</DialogTitle>
            <DialogDescription>
              Lands you in the editor — upload your branded PDF, drag company-wide placeholders (address, phone, page number) onto it, then share to as many templates as you like.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Letterhead name</Label>
            <Input
              autoFocus
              value={creatingLh.name}
              onChange={(e) => setCreatingLh({ ...creatingLh, name: e.target.value })}
              placeholder="e.g. Lutrom 2026 letterhead"
              data-testid="lh-create-name"
              onKeyDown={(e) => { if (e.key === 'Enter') submitCreateLh(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatingLh({ open: false, name: '' })}>Cancel</Button>
            <Button onClick={submitCreateLh} data-testid="lh-create-submit">Create & open editor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share letterhead dialog */}
      <Dialog open={sharing.open} onOpenChange={(o) => { if (!o) setSharing({ open: false, lh: null, selected: new Set() }); }}>
        <DialogContent data-testid="lh-share-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Share "{sharing.lh?.name}" with templates</DialogTitle>
            <DialogDescription>
              Tick the templates that should use this letterhead. Saving attaches/detaches them in one go — every linked template now picks up updates to the shared letterhead automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto -mx-6 px-6 space-y-1.5 border-y border-slate-100 py-2">
            {templates.length === 0 && (
              <div className="text-sm text-slate-500 py-4 text-center">No templates yet — create one first.</div>
            )}
            {templates.map((t) => {
              const checked = sharing.selected.has(t.id);
              const otherLh = t.letterheadId && t.letterheadId !== sharing.lh?.id;
              return (
                <label
                  key={t.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  data-testid={`lh-share-row-${t.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleShareSelection(t.id)}
                    data-testid={`lh-share-toggle-${t.id}`}
                  />
                  <span className="flex-1 truncate">
                    <span className="text-slate-900">{t.name}</span>
                    <span className="ml-2 text-xs text-slate-400 font-mono">{t.kind}</span>
                  </span>
                  {otherLh && !checked && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                      uses another
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSharing({ open: false, lh: null, selected: new Set() })}>Cancel</Button>
            <Button onClick={submitShare} data-testid="lh-share-submit">
              Save ({sharing.selected.size} selected)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PdfTemplates;
